import { Link } from "react-router-dom";
import { ArrowRight, Compass, Wrench, ShieldCheck, Crown, CheckCircle2, Zap, CalendarCheck, CreditCard } from "lucide-react";
import { SITE, FRAMEWORK_LEVELS } from "@/lib/site";
import PageHero, { ScheduleConsultationButton } from "@/components/site/PageHero";

const ICONS = { Compass, Wrench, ShieldCheck, Crown, Zap };

const LEVEL_DETAILS = {
  1: ["Comprehensive diagnostic review","Identification of compliance gaps","Strengths & opportunities report","Recommended next steps"],
  2: ["3 months of bank statements reviewed","Immediate concerns surfaced","Risk & opportunity flags","Actionable next-step recommendations"],
  3: ["Bookkeeping reconstruction","Compliance correction","Reporting cleanup","Tax position rebuild"],
  4: ["Monthly oversight cadence","Advisory check-ins","Reporting & dashboards","Compliance monitoring"],
  5: ["Executive financial leadership","Strategic direction","Board-level reporting","Compliance oversight","Quarterly executive sessions","Priority year-round access"],
};

export default function ExecutiveFinancialCommand() {
  return (
    <div data-testid="efc-page">
      <PageHero
        eyebrow="A Premium Invitation"
        title={
          <>
            Executive Financial Command<span className="text-[var(--trj-gold)]">™</span>
          </>
        }
        subtitle="Our highest level of support — designed for organizations seeking executive financial leadership, compliance oversight, and strategic direction beyond basic tax preparation."
        image={SITE.assets.boardroom}
      >
        <ScheduleConsultationButton variant="gold" testid="efc-hero-apply-btn" label="Apply Now" calendlyOverride={"https://calendly.com/trjservicesinc/consultation"} />
        <Link to="/contact" className="trj-btn-outline-white" data-testid="efc-hero-contact-btn">
          Ask a Question
        </Link>
      </PageHero>

      {/* INTRO */}
      <section className="bg-white py-20 md:py-28" data-testid="efc-intro">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="trj-eyebrow mb-4">The TRJ Framework</div>
          <h2 className="font-serif-trj text-4xl md:text-5xl text-[var(--trj-navy)] leading-tight">
            Five progressive levels of financial command.
          </h2>
          <p className="mt-6 text-[var(--trj-muted)] leading-relaxed max-w-3xl mx-auto">
            From a focused diagnostic and a 72-hour rapid cleanup, through full cleanup, ongoing oversight,
            and executive leadership over your entire financial function — the TRJ framework meets your
            organization exactly where it is and moves it forward with structure.
          </p>
        </div>
      </section>

      {/* LEVELS GRID */}
      <section className="bg-trj-navy-gradient py-20 md:py-28 text-white relative overflow-hidden" data-testid="efc-levels">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full" style={{background:"radial-gradient(closest-side, rgba(212,175,55,0.3), transparent)"}}/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {FRAMEWORK_LEVELS.map((lvl) => {
              const Ico = ICONS[lvl.icon] || Compass;
              const details = LEVEL_DETAILS[lvl.level];
              const premium = lvl.premium;
              return (
                <div
                  key={lvl.level}
                  data-testid={`efc-level-card-${lvl.level}`}
                  className={
                    premium
                      ? "trj-card-premium relative md:col-span-2 lg:col-span-1 lg:scale-[1.04] flex flex-col"
                      : "bg-white text-[var(--trj-navy)] p-7 border border-white/10 flex flex-col"
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className={`text-[10px] tracking-[0.25em] uppercase font-bold ${premium ? "text-[var(--trj-gold)]" : "text-[var(--trj-green)]"}`}>
                      Level {lvl.level}
                    </div>
                    <div className={`w-9 h-9 flex items-center justify-center ${premium ? "bg-[var(--trj-gold)] text-[var(--trj-navy)]" : "bg-[var(--trj-navy)] text-[var(--trj-gold)]"}`}>
                      <Ico className="w-4 h-4" />
                    </div>
                  </div>
                  {lvl.badge && (
                    <div className={`mt-3 inline-flex self-start text-[9px] uppercase tracking-widest px-2 py-0.5 ${premium ? "bg-[var(--trj-gold)]/20 text-[var(--trj-gold)]" : "bg-[var(--trj-gold)]/15 text-[var(--trj-gold-dark)]"}`}>
                      {lvl.badge}
                    </div>
                  )}
                  <h3 className={`font-serif-trj text-2xl mt-4 leading-snug ${premium ? "text-white" : ""}`}>{lvl.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${premium ? "text-white/80" : "text-[var(--trj-muted)]"}`}>
                    {lvl.description}
                  </p>
                  <ul className={`mt-5 space-y-2 text-[13px] ${premium ? "text-white/85" : "text-[var(--trj-navy)]"}`}>
                    {details.map((d) => (
                      <li key={d} className="flex items-start gap-2">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 ${premium ? "text-[var(--trj-gold)]" : "text-[var(--trj-green)]"}`} />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <LevelActions lvl={lvl} premium={premium} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO ITS FOR */}
      <section className="bg-white py-20 md:py-28" data-testid="efc-who">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="trj-eyebrow mb-4">Designed For</div>
            <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">
              Leaders who need executive financial command &mdash; not another vendor.
            </h2>
            <div className="trj-divider-gold mt-6" />
            <ul className="mt-6 space-y-3 text-[var(--trj-navy)]">
              {[
                "Growing businesses navigating complexity",
                "Nonprofits requiring board-level reporting",
                "Ministries needing stewardship leadership",
                "Multi-entity owners with consolidation needs",
                "Founders preparing for funding or sale",
              ].map((it) => (
                <li key={it} className="flex items-start gap-3"><span className="w-2 h-2 mt-2 bg-[var(--trj-gold)]" /> {it}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <img src={SITE.assets.meeting} alt="Executive meeting" className="w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--trj-green)] py-20 text-white text-center" data-testid="efc-cta">        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-serif-trj text-3xl md:text-5xl leading-tight">
            Take command of your financial future.
          </h2>
          <p className="mt-5 text-white/90 leading-relaxed">
            Applications are reviewed personally. We&apos;ll determine fit, scope, and the right starting tier.
          </p>
          <div className="mt-8 flex justify-center flex-wrap gap-3">
            <ScheduleConsultationButton variant="gold" testid="efc-final-apply-btn" label="Apply Now" calendlyOverride={"https://calendly.com/trjservicesinc/consultation"} />
            <Link to="/contact" className="trj-btn-outline-white" data-testid="efc-final-contact-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function LevelActions({ lvl, premium }) {
  const cta = lvl.cta || {};
  if (cta.type === "book") {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className="trj-btn-green text-sm mt-7 w-full justify-center"
        data-testid={`efc-level-${lvl.level}-book-btn`}
      >
        <CalendarCheck className="w-4 h-4" /> {cta.label || "Book"}
      </a>
    );
  }
  if (cta.type === "pay") {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        className="trj-btn-gold text-sm mt-7 w-full justify-center"
        data-testid={`efc-level-${lvl.level}-pay-btn`}
      >
        <CreditCard className="w-4 h-4" /> {cta.label || "Get Started"}
      </a>
    );
  }
  // Default: consult (Schedule + Review)
  const greenCls = premium ? "trj-btn-gold" : "trj-btn-green";
  const outlineCls = premium
    ? "border border-white/40 text-white hover:bg-white hover:text-[var(--trj-navy)] transition-colors px-4 py-2.5 inline-flex items-center justify-center gap-2 font-semibold text-sm w-full"
    : "border border-[var(--trj-navy)] text-[var(--trj-navy)] hover:bg-[var(--trj-navy)] hover:text-white transition-colors px-4 py-2.5 inline-flex items-center justify-center gap-2 font-semibold text-sm w-full";
  return (
    <div className="mt-7 flex flex-col gap-2">
      <a
        href={SITE.consultationUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${greenCls} text-sm w-full justify-center`}
        data-testid={`efc-level-${lvl.level}-schedule-btn`}
      >
        Schedule a Consultation
      </a>
      <Link
        to="/contact"
        className={outlineCls}
        data-testid={`efc-level-${lvl.level}-review-btn`}
      >
        Request a Financial Review
      </Link>
    </div>
  );
}
